import { useState, useEffect } from "react";
import { Play, X, ArrowsOut } from "@phosphor-icons/react";
import { api } from "../lib/api";

// Fallback ID used until the /api/settings/tutorial-video request resolves.
// Also used if an admin has never set a custom video.
const FALLBACK_VIDEO_ID = "FBrmuv7QUPk";
const thumbSrc = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const embedSrc = (id, autoplay = false) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1&playsinline=1`;

const ACCENT = "#FFD60A";

/**
 * Onboarding tutorial banner. Hybrid embed pattern: real YouTube thumbnail +
 * play badge until the user clicks Play; then swap in the iframe. Also offers
 * a "Watch fullscreen" lightbox for a larger view on desktop.
 *
 * The video shown is admin-configurable via GET /api/settings/tutorial-video.
 */
export default function TutorialBanner({ onDismiss, variant = "returning" }) {
  const [playing, setPlaying] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [videoId, setVideoId] = useState(FALLBACK_VIDEO_ID);
  const isNew = variant === "new";

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/tutorial-video").then((r) => {
      if (!cancelled && r.data?.video_id) setVideoId(r.data.video_id);
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Escape closes the fullscreen lightbox
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    // Prevent page-scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen]);

  return (
    <>
      <section
        data-testid="tutorial-banner"
        className={`relative mb-8 rounded-2xl border overflow-hidden ${
          isNew
            ? "border-amber-400/40 bg-gradient-to-br from-amber-400/[0.09] to-transparent shadow-[0_0_60px_-20px_rgba(255,214,10,0.4)]"
            : "border-white/10 bg-[#121212]"
        }`}
      >
        <div className="grid sm:grid-cols-[200px_1fr] gap-4 sm:gap-6 items-center p-4 sm:p-5">
          <button
            onClick={() => setPlaying(true)}
            data-testid="tutorial-banner-play"
            disabled={playing}
            className="relative w-[180px] max-w-[60vw] mx-auto sm:mx-0 sm:w-full sm:max-w-none aspect-[9/16] bg-black overflow-hidden rounded-xl group flex-shrink-0 disabled:cursor-default"
            aria-label="Play tutorial video"
          >
            {playing ? (
              <iframe
                data-testid="tutorial-banner-iframe"
                src={embedSrc(videoId, true)}
                title="FlyerStudio tutorial"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img
                  src={thumbSrc(videoId)}
                  alt="Tutorial thumbnail"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                  <span
                    className="h-14 w-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110"
                    style={{ backgroundColor: ACCENT }}
                  >
                    <Play weight="fill" size={22} className="text-black ml-0.5" />
                  </span>
                </div>
              </>
            )}
          </button>

          <div className="flex flex-col justify-center text-center sm:text-left">
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300/90 font-bold mb-1.5">
              {isNew ? "New here?" : "Quick tutorial"}
            </p>
            <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight text-white mb-1.5">
              Make your first flyer in 2 minutes
            </h2>
            <p className="text-sm text-zinc-400 max-w-xl leading-snug">
              A 120-second walkthrough — upload an image, tweak the headline, hit
              Generate. That&apos;s it.
            </p>
            {!playing && (
              <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
                <button
                  onClick={() => setPlaying(true)}
                  data-testid="tutorial-banner-play-cta"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm text-black transition-transform hover:scale-105"
                  style={{ backgroundColor: ACCENT }}
                >
                  <Play weight="fill" size={14} /> Watch now
                </button>
                <button
                  onClick={() => setFullscreen(true)}
                  data-testid="tutorial-banner-fullscreen-cta"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm text-zinc-200 bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                >
                  <ArrowsOut weight="bold" size={14} /> Watch fullscreen
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={onDismiss}
          data-testid="tutorial-banner-dismiss"
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/40 backdrop-blur hover:bg-black/60 text-zinc-300 hover:text-white flex items-center justify-center transition-colors"
          aria-label="Dismiss tutorial banner"
          title="Dismiss (use the ? in the header to bring it back)"
        >
          <X weight="bold" size={14} />
        </button>
      </section>

      {/* Fullscreen lightbox — bigger viewing surface on desktop */}
      {fullscreen && (
        <div
          data-testid="tutorial-lightbox"
          className="fixed inset-0 z-[1200] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
          onClick={() => setFullscreen(false)}
          role="dialog"
          aria-label="Tutorial video (fullscreen)"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative aspect-[9/16] w-auto h-[92vh] max-w-full bg-black rounded-2xl overflow-hidden shadow-2xl"
          >
            <iframe
              data-testid="tutorial-lightbox-iframe"
              src={embedSrc(videoId, true)}
              title="FlyerStudio tutorial (fullscreen)"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <button
            onClick={() => setFullscreen(false)}
            data-testid="tutorial-lightbox-close"
            className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X weight="bold" size={20} />
          </button>
        </div>
      )}
    </>
  );
}
